import type { ApiClient } from '@carbonwebmail/api-client'
import type { SuppressionRowDto } from '@carbonwebmail/contracts'
import { useCallback, useState } from 'react'
import { Alert } from '../../ui/Alert.js'
import { CreateCard } from '../../ui/CreateCard.js'
import { Field, SelectField } from '../../ui/Field.js'
import { when } from '../../ui/format.js'
import { EmptyCard, PagedScreen } from '../../ui/PagedScreen.js'
import { StatusPill } from '../../ui/StatusPill.js'
import { Td, Tr } from '../../ui/Table.js'
import { useMay } from '../../ui/yourRole.js'
import { REASONS } from './statusLabels.js'
import type { SuppressionReasonFilter } from './useSuppressions.js'
import { confirmationFor, useSuppressions } from './useSuppressions.js'

/**
 * Addresses this organisation may not send to.
 *
 * The reason column is the screen. "Suppressed" alone is useless — an
 * unsubscribe is a person's choice and permanent, a hard bounce is a dead
 * mailbox, and a complaint is someone reporting you as spam, which is the one
 * that costs a sending reputation. Collapsing them into one state is what lets
 * a team keep mailing an address that has already reported them.
 *
 * Removing is the dangerous direction, so the confirmation names the
 * consequence of removing *that* kind rather than asking "are you sure". "Are
 * you sure" is a question nobody has ever answered no to; "they asked to stop
 * hearing from you, and mailing them again is unlawful in most places" is a
 * sentence somebody reads.
 *
 * Every removal is written to the consent record. "You unsubscribed me and kept
 * mailing" has exactly two possible answers, and this is what lets the right
 * one be evidenced. (I6)
 */

const FILTERS: readonly (readonly [string, string])[] = [
  ['all', 'Every reason'],
  ['unsubscribed', 'Unsubscribed'],
  ['hard_bounce', 'Hard bounce'],
  ['complaint', 'Complaint'],
  ['manual', 'Added by hand'],
]

export function SuppressionsPage({ client }: { readonly client: ApiClient }) {
  const suppressions = useSuppressions({ client })
  const { state, reason, notice, problem, busy } = suppressions

  /*
   * Suppressions are guarded as `domains` on the server, so a member and a
   * viewer may read the list and change nothing on it. Reading it is the point
   * for them — "why did this address not get the campaign" is answered here.
   */
  const mayWrite = useMay('domains', 'write')

  const [email, setEmail] = useState('')
  const [note, setNote] = useState('')

  const add = useCallback(async () => {
    await suppressions.suppress(email, note)
    setEmail('')
    setNote('')
  }, [suppressions, email, note])

  /**
   * The browser's own confirm, deliberately.
   *
   * It is modal, it cannot be missed, and it is the one dialogue somebody
   * cannot click past while looking somewhere else. A prettier in-page
   * confirmation is one people learn to dismiss, and this is the action that
   * re-enables mail to somebody who asked to stop.
   */
  const remove = useCallback(
    async (row: SuppressionRowDto) => {
      if (!window.confirm(confirmationFor(row))) return
      await suppressions.unsuppress(row.email)
    },
    [suppressions],
  )

  return (
    <PagedScreen
      title="Suppression list"
      intro="Addresses no campaign will reach, and why. Read at the moment each message is claimed, never cached from when it was queued."
      state={state}
      onRetry={() => void suppressions.retry()}
      onPage={suppressions.setPage}
      before={
        <>
          {problem === undefined ? null : <Alert>{problem}</Alert>}
          {notice === undefined ? null : <Alert tone="ok">{notice}</Alert>}

          {!mayWrite ? null : (
            <CreateCard
              heading="Add one by hand"
              submit="Add to the list"
              disabled={busy || email.trim() === '' || note.trim() === ''}
              onSubmit={() => void add()}
            >
              <Field
                id="suppress-email"
                label="Email address"
                type="email"
                autoComplete="off"
                value={email}
                disabled={busy}
                hint="No campaign will reach it from the moment you add it."
                onChange={setEmail}
              />
              <Field
                id="suppress-note"
                label="Why"
                type="text"
                autoComplete="off"
                value={note}
                disabled={busy}
                hint="Whatever happened, happened outside this product. This sentence is the only trace of it there will ever be."
                onChange={setNote}
              />
            </CreateCard>
          )}

          <div className="mb-6 max-w-xs">
            <SelectField
              id="suppression-reason"
              label="Show"
              value={reason}
              disabled={busy}
              options={FILTERS}
              onChange={(value) => suppressions.setReason(value as SuppressionReasonFilter)}
            />
          </div>
        </>
      }
      head={['Email', 'Reason', 'Added', '']}
      minWidth={640}
      empty={
        reason === 'all' ? (
          <EmptyCard heading="Nothing is suppressed">
            Unsubscribes, hard bounces and complaints land here automatically. An empty list means
            none have happened yet — not that suppression is switched off.
          </EmptyCard>
        ) : (
          /* A different sentence, because "nothing is suppressed" would be
             false: the list is not empty, this slice of it is. */
          <EmptyCard heading="Nothing for that reason">
            Other addresses are suppressed for other reasons. Choose "Every reason" to see them.
          </EmptyCard>
        )
      }
      row={(item) => (
        <Tr key={`${item.email}-${item.createdAt}`}>
          <Td mono>{item.email}</Td>
          <Td>
            <StatusPill tone={REASONS[item.reason].tone}>{REASONS[item.reason].label}</StatusPill>
          </Td>
          <Td>{when(item.createdAt)}</Td>
          <Td>
            {!mayWrite ? null : (
              <button
                type="button"
                disabled={busy}
                onClick={() => void remove(item)}
                className="font-medium text-[13px] text-t3 hover:text-danger disabled:opacity-60"
              >
                Remove
              </button>
            )}
          </Td>
        </Tr>
      )}
    />
  )
}
